import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
} from "@mui/material";
import { Gavel as GavelIcon } from "@mui/icons-material";

const TermsOfServicePage: React.FC = () => {
  const lastUpdated = "January 8, 2026";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundImage: "url('/leaves-removebg-preview.png')",
        backgroundBlendMode: "soft-light",
      }}
    >
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <GavelIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
            <Typography
              variant="h3"
              component="h1"
              fontWeight={700}
              gutterBottom
            >
              Terms of Service
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last Updated: {lastUpdated}
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Alert severity="info" sx={{ mb: 4 }}>
            By creating an account or using Sanduuq, you agree to these Terms of
            Service. Please read them carefully.
          </Alert>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms of Service ("Terms") govern your access to and use of
              Sanduuq's lending circle platform, including our website, mobile
              applications, and related services (collectively, the "Service").
              By accessing or using the Service, you agree to be bound by these
              Terms and our Privacy Policy.
            </Typography>
            <Typography variant="body1" paragraph>
              If you do not agree to these Terms, you may not access or use the
              Service.
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              2. Description of Service
            </Typography>
            <Typography variant="body1" paragraph>
              Sanduuq provides a platform that enables users to create, join,
              and participate in lending circles (also known as rotating savings
              and credit associations or ROSCAs). A lending circle is a group of
              individuals who agree to contribute a fixed amount periodically,
              with each member receiving the total pool on a rotating basis.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Important:</strong> Sanduuq is a technology platform only.
              We do not provide financial services, act as a lender, or
              guarantee any transactions between users.
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              3. Eligibility
            </Typography>
            <Typography variant="body1" paragraph>
              To use Sanduuq, you must:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Be at least 18 years of age" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Be a legal resident of the United States" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Have a valid bank account in your name" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Provide accurate identity verification information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Not be prohibited from using financial services under applicable law" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              4. Account Registration
            </Typography>
            <Typography variant="body1" paragraph>
              You must create an account to use most features of Sanduuq. When
              registering, you agree to:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Provide accurate, current, and complete information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Maintain and update your information as needed" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Keep your login credentials confidential" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Accept responsibility for all activity under your account" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Notify us immediately of any unauthorized access" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              5. Lending Circle Participation
            </Typography>

            <Typography
              variant="h6"
              fontWeight={500}
              sx={{ mt: 3 }}
              gutterBottom
            >
              5.1 Creating a Circle
            </Typography>
            <Typography variant="body1" paragraph>
              Circle creators ("Administrators") are responsible for:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Setting circle terms (contribution amount, frequency, member limit)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Approving or rejecting member requests" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Managing the payout rotation order" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Ensuring fair treatment of all members" />
              </ListItem>
            </List>

            <Typography
              variant="h6"
              fontWeight={500}
              sx={{ mt: 3 }}
              gutterBottom
            >
              5.2 Joining a Circle
            </Typography>
            <Typography variant="body1" paragraph>
              By joining a circle, you agree to:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Make all required contributions on time" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Remain in the circle until completion" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Accept the payout order determined for the circle" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Communicate promptly about any payment issues" />
              </ListItem>
            </List>

            <Typography
              variant="h6"
              fontWeight={500}
              sx={{ mt: 3 }}
              gutterBottom
            >
              5.3 Payment Obligations
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Your payment obligations are binding commitments.</strong>{" "}
              When you join a circle and the circle becomes active, you are
              legally obligated to make all contributions for the duration of
              the circle, regardless of whether you have received your payout
              yet.
            </Typography>
            <Typography variant="body1" paragraph>
              Failure to make payments may result in:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Late fees as specified in circle terms" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Negative impact on your Sanduuq reputation" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Account suspension or termination" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Collection actions for outstanding amounts" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Reporting to credit agencies in severe cases" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              6. Fees
            </Typography>
            <Typography variant="body1" paragraph>
              Sanduuq may charge fees for using the Service, including:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Platform fees on circle contributions" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Payment processing fees" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Late payment penalties" />
              </ListItem>
            </List>
            <Typography variant="body1" paragraph>
              All applicable fees will be disclosed before you complete any
              transaction. We reserve the right to modify fees with 30 days'
              notice.
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              7. Prohibited Conduct
            </Typography>
            <Typography variant="body1" paragraph>
              You agree not to:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Use the Service for any illegal purpose" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Provide false or misleading information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Create multiple accounts" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Attempt to manipulate or defraud other users" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Interfere with the proper functioning of the Service" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Use the Service for money laundering or terrorist financing" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Harass, threaten, or intimidate other users" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Violate any applicable laws or regulations" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              8. Account Suspension & Termination
            </Typography>
            <Typography variant="body1" paragraph>
              We may suspend or terminate your account if you:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Violate these Terms" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Fail to meet payment obligations" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Engage in fraudulent activity" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Pose a risk to other users or the platform" />
              </ListItem>
            </List>
            <Typography variant="body1" paragraph>
              You may request account deletion at any time, subject to
              fulfilling any outstanding circle obligations.
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              9. Dispute Resolution
            </Typography>
            <Typography variant="body1" paragraph>
              Disputes between circle members should first be resolved through
              direct communication. Sanduuq may provide mediation assistance but
              is not obligated to resolve disputes.
            </Typography>
            <Typography variant="body1" paragraph>
              Any disputes between you and Sanduuq shall be resolved through
              binding arbitration in accordance with the American Arbitration
              Association rules. You waive any right to participate in class
              action lawsuits.
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              10. Disclaimers
            </Typography>
            <Typography variant="body1" paragraph>
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.
              SANDOOQ DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
              NON-INFRINGEMENT.
            </Typography>
            <Typography variant="body1" paragraph>
              We do not guarantee that the Service will be uninterrupted,
              error-free, or secure. You use the Service at your own risk.
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              11. Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, SANDOOQ SHALL NOT BE
              LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
              PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER
              INCURRED DIRECTLY OR INDIRECTLY.
            </Typography>
            <Typography variant="body1" paragraph>
              OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM THESE TERMS SHALL
              NOT EXCEED THE FEES YOU PAID TO US IN THE 12 MONTHS PRECEDING THE
              CLAIM.
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              12. Indemnification
            </Typography>
            <Typography variant="body1" paragraph>
              You agree to indemnify and hold harmless Sanduuq, its officers,
              directors, employees, and agents from any claims, damages, losses,
              or expenses arising from your use of the Service or violation of
              these Terms.
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              13. Changes to Terms
            </Typography>
            <Typography variant="body1" paragraph>
              We may modify these Terms at any time. Material changes will be
              communicated via email or in-app notification at least 30 days
              before taking effect. Your continued use of the Service after
              changes constitutes acceptance of the new Terms.
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              14. Governing Law
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms shall be governed by the laws of the State of
              California, without regard to conflict of law principles. Any
              legal action must be brought in the courts located in San
              Francisco County, California.
            </Typography>
          </Box>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              15. Severability
            </Typography>
            <Typography variant="body1" paragraph>
              If any provision of these Terms is found unenforceable, the
              remaining provisions will continue in full force and effect.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Contact Us
            </Typography>
            <Typography variant="body1">
              For questions about these Terms:
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Email: legal@sandooq.com
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfServicePage;
